import useSWR from 'swr';
import { transactionsDpRecommendation } from 'integration/generated/transactions';
import {
	DpIARecommendationSizeMode,
	DpIARecommendationSizes,
	DpIARecommendationType,
} from 'integration/generated/transactions/data-contracts';
import { useSettings } from '@/data/Settings';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';

const DEFAULT_RECOMMENDATION_TYPES: DpIARecommendationType[] = ['outfit', 'recommendations'];

const OPTIONAL_RECOMMENDATION_TYPES: DpIARecommendationType[] = ['sameBrand'];

export type DpIARecommendationOptions = {
	types?: DpIARecommendationType[];
	sizes?: DpIARecommendationSizes;
	sizeMode?: DpIARecommendationSizeMode;
};

export const iaRecommendation =
	(pub = true) =>
	async (
		storeId: string,
		partnumber: string,
		query: any,
		params: any,
		filters: Array<any> = [],
		{ types = DEFAULT_RECOMMENDATION_TYPES, sizes, sizeMode }: DpIARecommendationOptions = {}
	) => {
		const request = (type: DpIARecommendationType[], withSizes: boolean) =>
			transactionsDpRecommendation(pub).iaRecommendation(
				storeId,
				{
					type,
					rules: {
						params: {
							partnumber,
							limit: 20,
							...(withSizes && sizes ? { sizes } : {}),
							...(withSizes && sizeMode ? { sizeMode } : {}),
						},
						filters: {
							values: filters,
						},
						priority: {
							values: [],
						},
					},
				},
				query,
				params
			);
		const fallbackTypes = types.filter((type) => !OPTIONAL_RECOMMENDATION_TYPES.includes(type));
		const hasOptionalParams = fallbackTypes.length < types.length || !!sizes || !!sizeMode;

		try {
			return await request(types, true);
		} catch {
			return hasOptionalParams ? request(fallbackTypes, false).catch(() => undefined) : undefined;
		}
	};

const DATA_KEY_DP_IA_RECOMMENDATION = 'dpIaRecommendation';

export const useGetDpIARecommendation = (
	partNumber?: string,
	filters: Array<any> = [],
	{ types, sizes, sizeMode }: DpIARecommendationOptions = {}
) => {
	const { settings } = useSettings();
	const router = useNextRouter();
	const { storeId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();

	const baseQuery = useSWR(
		storeId && partNumber
			? [
					{
						storeId,
						partNumber,
						filters,
						...(types ? { types } : {}),
						...(sizes ? { sizes } : {}),
						...(sizeMode ? { sizeMode } : {}),
					},
					DATA_KEY_DP_IA_RECOMMENDATION,
			  ]
			: null,
		async ([props]) =>
			await iaRecommendation(true)(props.storeId, props.partNumber, {}, params, props.filters, {
				types: props.types,
				sizes: props.sizes,
				sizeMode: props.sizeMode,
			})
	);

	return baseQuery;
};
